import { create } from 'zustand';
import { PaginationType } from '../types/pagination';
import {
  createReview,
  CreateReviewRequest,
  fetchReviews,
  fetchReviewsExport,
  FetchReviewsRequest,
  ReviewCategoryType,
  ReviewsExportFormatType,
  ReviewsExportRequest,
  ReviewType,
} from './api/reviews-api';
import { useAlertStore } from './use-alert-store';

interface Store {
  isLoadingFetch: boolean;
  loadingExportFormat?: ReviewsExportFormatType;
  reviews: ReviewType[];
  fetchReviews: (payload: FetchReviewsRequest) => Promise<void>;
  fetchReviewsExport: (payload: ReviewsExportRequest) => Promise<void>;
  negative?: ReviewCategoryType;
  neutral?: ReviewCategoryType;
  positive?: ReviewCategoryType;
  pagination?: PaginationType;
  createReview: (payload: CreateReviewRequest) => Promise<void>;
  handleUpdateReviews: (payload: ReviewType) => void;
}

export const useReviewsStore = create<Store>((set, get) => ({
  isLoadingFetch: false,
  reviews: [],
  handleUpdateReviews: (payload) => {
    set((state) => {
      const isExist = state.reviews.some((item) => item.id === payload.id);
      const isSameTaskType = state.reviews[0]?.task_type === payload.task_type;

      if (!isExist && isSameTaskType) {
        return { reviews: [payload, ...state.reviews] };
      }
      const reviews = state.reviews.map((item) => (item.id === payload.id ? payload : item));
      return { reviews };
    });
  },
  fetchReviews: async (params) => {
    set({ isLoadingFetch: true });

    try {
      const { reviews, positive, neutral, negative, ...pagination } = await fetchReviews(params);

      const isInfinite = !!params.infinite;

      const updatedReviews = isInfinite ? get().reviews.concat(reviews) : reviews;
      set({ reviews: updatedReviews, positive, neutral, negative, pagination });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по отзывам', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },

  fetchReviewsExport: async (params) => {
    set({ loadingExportFormat: params.format });

    try {
      await fetchReviewsExport(params);
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по отзывам', variant: 'danger' });
    } finally {
      set({ loadingExportFormat: undefined });
    }
  },

  createReview: async (payload) => {
    set({ isLoadingFetch: true });
    try {
      await createReview(payload);
      useAlertStore.getState().add({ label: 'Отзыв успешно создан', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось создать отзыв', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoadingFetch: false });
    }
  },
}));
